/**
 * DrumNoteHighway — falling notes for the drum challenges.
 *
 * One lane per pad. Each step in the phrase becomes a note that falls toward
 * the hit line at the bottom, arriving exactly when the step is due. The lane
 * a note falls in tells you *which* pad; its distance from the line tells you
 * *when*.
 *
 * This is the drum feature's own visualiser, written for a variable number of
 * pads rather than the piano game's fixed six lanes, and sharing no code with
 * it. Notes are plain DOM elements repositioned per frame — with at most a few
 * dozen on screen there is nothing here that would justify a canvas.
 *
 * The clock is the audio clock, matching `useDrumChallenge`. Animating on any
 * other clock would let the notes drift away from the judging that decides
 * whether a hit was on time.
 */

import React, { useEffect, useRef, useState } from 'react';
import * as Tone from 'tone';
import { InstrumentPad } from '../../../data/africanInstruments';
import { ChallengeStep } from '../data/challenges';
import { DrumHitRating } from '../lib/scoring';

export interface DrumNoteHighwayProps {
  pads: InstrumentPad[];
  steps: ChallengeStep[];
  /**
   * Audio-clock time (ms) at which step 0 is due. Null whenever no phrase is
   * running, which parks the highway in its empty state.
   */
  phraseStartMs: number | null;
  /**
   * How far ahead a note becomes visible, and therefore how long it takes to
   * fall: a note appears at the top exactly this long before it is due.
   */
  lookAheadMs?: number;
  /** Dims the lanes during the demo, so "watch" reads differently from "play". */
  isDemo?: boolean;
  /**
   * The most recent judgement, which drives the hit flash. The `nonce` is what
   * makes a repeat of the same rating on the same pad animate again, since the
   * rating and pad alone would not change between two identical hits.
   */
  lastHit?: { rating: DrumHitRating; padIndex: number; nonce: number } | null;
}

/** Milliseconds on the audio clock — the same source the game loop judges on. */
const nowMs = (): number => Tone.getContext().currentTime * 1000;

const DEFAULT_LOOK_AHEAD_MS = 2000;

/**
 * Note geometry, in rem. These are the single source of truth: the styles
 * below and the travel measurement above both derive from them, so the note
 * cannot drift away from the distance it is animated over.
 *
 * The note is deliberately short. A tall block makes "on the line" ambiguous —
 * its top edge arrives well before its bottom edge, and at this fall speed that
 * gap is wider than the whole perfect window. A thin bar with a bright leading
 * edge has one unmistakable moment of contact.
 */
const NOTE_HEIGHT_REM = 0.75;
const NOTE_START_OFFSET_REM = 1;
/** Note width as a percentage of its lane, centred on the lane's midpoint. */
const NOTE_WIDTH_PCT = 72;

/** Lane accent colours, cycled across however many pads the drum has. */
const LANE_COLORS = ['#f5a623', '#e07a5f', '#81b29a', '#d4a574'];

/** Flash colour for each rating — green good, amber close, red missed. */
const HIT_COLORS: Record<DrumHitRating, string> = {
  PERFECT: '#81b29a',
  GOOD: '#f5a623',
  MISS: '#e07a5f',
};

/** How long the hit flash stays on screen. */
const HIT_FLASH_MS = 320;

export const DrumNoteHighway: React.FC<DrumNoteHighwayProps> = ({
  pads,
  steps,
  phraseStartMs,
  lookAheadMs = DEFAULT_LOOK_AHEAD_MS,
  isDemo = false,
  lastHit = null,
}) => {
  /** One node per step, created once by React and then only repositioned. */
  const noteRefs = useRef<(HTMLDivElement | null)[]>([]);
  const frameRef = useRef<number>();
  /** The track, so the fall distance can be measured against the real layout. */
  const trackRef = useRef<HTMLDivElement>(null);
  /** The hit line — what a note must arrive at, not the track floor. */
  const lineRef = useRef<HTMLDivElement>(null);

  /** The judgement currently being flashed, or null between hits. */
  const [flash, setFlash] = useState<{
    rating: DrumHitRating;
    padIndex: number;
  } | null>(null);

  // Show a burst on the struck lane, then clear it. Keyed on the nonce so two
  // identical hits in a row still produce two separate flashes.
  useEffect(() => {
    if (!lastHit) {
      setFlash(null);
      return undefined;
    }

    setFlash({ rating: lastHit.rating, padIndex: lastHit.padIndex });
    const timer = window.setTimeout(() => setFlash(null), HIT_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [lastHit?.nonce, lastHit?.rating, lastHit?.padIndex, lastHit]);

  useEffect(() => {
    // Parked: hide every note and run no animation frame at all.
    if (phraseStartMs === null) {
      noteRefs.current.forEach((node) => {
        if (node) node.style.opacity = '0';
      });
      return undefined;
    }

    /**
     * How far a note falls, in pixels, from where it starts to the moment its
     * centre sits on the hit line. Measured from the DOM rather than assumed,
     * so the lane keeps working at any height or breakpoint.
     *
     * This cannot be a percentage transform: percentages in `translate`
     * resolve against the element's *own* height, so `100%` moved a note down
     * by its own 24px instead of the ~296px to the line — which is why a note
     * became due while it was still up at the top of the track.
     */
    const measureTravel = (): number => {
      const track = trackRef.current;
      const line = lineRef.current;
      if (!track || !line) return 0;

      const trackTop = track.getBoundingClientRect().top;
      const lineRect = line.getBoundingClientRect();
      // Centre of the line, relative to the track's top edge.
      const lineCentre = lineRect.top - trackTop + lineRect.height / 2;

      // The note's *leading* (bottom) edge is what meets the line, so that is
      // what the travel distance is measured against. Aligning by the centre
      // would leave the top edge touching early and the bottom edge late,
      // with no single frame that reads as "now".
      const rem =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const noteBottomAtStart =
        -NOTE_START_OFFSET_REM * rem + NOTE_HEIGHT_REM * rem;

      return lineCentre - noteBottomAtStart;
    };

    let travelPx = measureTravel();

    // The track is responsive; a resize changes how far a note has to fall.
    const onResize = () => {
      travelPx = measureTravel();
    };
    window.addEventListener('resize', onResize);

    const tick = () => {
      const elapsed = nowMs() - phraseStartMs;

      steps.forEach((step, index) => {
        const node = noteRefs.current[index];
        if (!node) return;

        // 0 when the note appears above the track, 1 when its centre meets
        // the line — which is exactly when the step is due to be played.
        const progress = (elapsed - (step.timeMs - lookAheadMs)) / lookAheadMs;

        // Not yet due to appear, or already well past the line.
        if (progress < 0 || progress > 1.25) {
          node.style.opacity = '0';
          return;
        }

        // Past the line the note stops and fades where it landed, rather than
        // carrying on down through the key legend. Freezing at the line keeps
        // the moment of contact as the last thing the eye sees.
        node.style.opacity = progress > 1 ? String(1 - (progress - 1) * 4) : '1';
        node.style.transform = `translate3d(0, ${Math.min(progress, 1) * travelPx}px, 0)`;
      });

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('resize', onResize);
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    };
  }, [lookAheadMs, phraseStartMs, steps]);

  return (
    <div
      ref={trackRef}
      className="relative w-full h-64 sm:h-80 rounded-2xl border border-studio-800 bg-studio-950 overflow-hidden"
      aria-hidden="true"
    >
      {/* Lanes, one per pad */}
      <div className="absolute inset-0 flex">
        {pads.map((pad, laneIndex) => {
          const color = LANE_COLORS[laneIndex % LANE_COLORS.length];

          return (
            <div
              key={pad.keyBinding}
              className="flex-1 relative border-r border-studio-800/60 last:border-r-0"
              style={{
                background: `linear-gradient(to bottom, transparent 60%, ${color}0d 100%)`,
              }}
            >
              {/* The falling notes belonging to this lane */}
              {steps.map((step, stepIndex) =>
                step.padIndex !== laneIndex ? null : (
                  <div
                    key={stepIndex}
                    ref={(node) => {
                      noteRefs.current[stepIndex] = node;
                    }}
                    className="absolute rounded-lg pointer-events-none will-change-transform"
                    style={{
                      // `top` sets the travel origin just above the track; the
                      // per-frame transform then carries it down to the line.
                      top: `${-NOTE_START_OFFSET_REM}rem`,
                      // Centred with left/width rather than a translate: the
                      // animation loop rewrites `transform` every frame, which
                      // would wipe out any horizontal centring kept there.
                      left: `${(100 - NOTE_WIDTH_PCT) / 2}%`,
                      width: `${NOTE_WIDTH_PCT}%`,
                      height: `${NOTE_HEIGHT_REM}rem`,
                      opacity: 0,
                      // Dim body, bright bottom edge: the bright edge is the
                      // part that lands on the line, so the eye tracks the
                      // exact thing the judging cares about.
                      background: `linear-gradient(180deg, ${color}55 0%, ${color} 85%)`,
                      borderBottom: `3px solid #f5f5f4`,
                      boxShadow: `0 2px 12px ${color}88`,
                    }}
                  />
                ),
              )}

              {/* Hit burst: a ring at the line plus a column glow up the lane */}
              {flash && flash.padIndex === laneIndex && (
                <React.Fragment key={`flash-${laneIndex}`}>
                  <div
                    className="absolute left-0 right-0 pointer-events-none drum-hit-glow"
                    style={{
                      bottom: '3rem',
                      height: '40%',
                      background: `linear-gradient(to top, ${
                        HIT_COLORS[flash.rating]
                      }44 0%, transparent 100%)`,
                    }}
                  />
                  <div
                    className="absolute left-1/2 pointer-events-none drum-hit-ring"
                    style={{
                      bottom: '3rem',
                      width: '2.75rem',
                      height: '2.75rem',
                      marginLeft: '-1.375rem',
                      marginBottom: '-1.375rem',
                      borderRadius: '9999px',
                      border: `2px solid ${HIT_COLORS[flash.rating]}`,
                    }}
                  />
                </React.Fragment>
              )}
            </div>
          );
        })}
      </div>

      {/* Hit line — where a note should be struck */}
      <div
        ref={lineRef}
        className="absolute left-0 right-0 bottom-12 h-[3px] bg-stone-100/70 shadow-[0_0_16px_rgba(255,255,255,0.35)]"
      />

      {/* Key legend under the line, so lanes map to fingers at a glance */}
      <div className="absolute left-0 right-0 bottom-0 h-12 flex items-center">
        {pads.map((pad, laneIndex) => (
          <div key={pad.keyBinding} className="flex-1 text-center">
            <span
              className="font-mono text-xs uppercase font-bold"
              style={{ color: LANE_COLORS[laneIndex % LANE_COLORS.length] }}
            >
              {pad.keyBinding}
            </span>
            <span className="block text-[10px] text-stone-600 leading-tight truncate px-1">
              {pad.label}
            </span>
          </div>
        ))}
      </div>

      {/*
        Keyframes scoped to this component's own tree. Deliberately not added
        to `index.css` and not injected into `document.head` at module scope:
        the drum feature stays removable in one delete.
      */}
      <style>{`
        @keyframes drumHitRing {
          0%   { transform: scale(0.4); opacity: 1; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes drumHitGlow {
          0%   { opacity: 0.85; }
          100% { opacity: 0; }
        }
        .drum-hit-ring { animation: drumHitRing ${HIT_FLASH_MS}ms ease-out forwards; }
        .drum-hit-glow { animation: drumHitGlow ${HIT_FLASH_MS}ms ease-out forwards; }
        @media (prefers-reduced-motion: reduce) {
          .drum-hit-ring, .drum-hit-glow { animation-duration: 1ms; }
        }
      `}</style>

      {/* During the demo the player is watching, not playing. */}
      {isDemo && (
        <div className="absolute inset-x-0 top-0 flex justify-center pt-2 pointer-events-none">
          <span className="text-sage-400 font-mono text-[10px] tracking-widest uppercase bg-studio-950/85 rounded-full px-3 py-1">
            &#9834; Watch and listen
          </span>
        </div>
      )}
    </div>
  );
};
